import { HttpClient } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { API_CONST } from "@core/constants/api.constants";
import { CanonicalPaymentReadOverrideService } from "@/app/core/services/canonical-payment-read-override.service";
import { environment } from "@environments/environment";
import { map } from "rxjs/operators";
import {
  normalizePayoutList,
  normalizeProviderBeneficiary,
  normalizeProviderPayout,
  normalizeProviderPayoutMethod,
  normalizeProviderWalletList,
  normalizeWalletOverviewDto,
  normalizeWalletTransactionList,
} from "./wallet-response-normalizers";

@Injectable({
  providedIn: "root",
})
export class WalletService {
  readonly apiUrl = environment.apiUrl;
  readonly http = inject(HttpClient);
  private readonly canonicalReadOverride = inject(CanonicalPaymentReadOverrideService);

  private withPaymentProviderReadsParam(params: Record<string, string | number>): Record<string, string | number> {
    const v = this.canonicalReadOverride.getQueryValueForHttp();
    if (v == null) {
      return params;
    }
    return { ...params, canonicalReads: v };
  }

  getWalletOverview(businessId: string) {
    const url = `${this.apiUrl}${API_CONST.WALLET.GET_WALLET_OVERVIEW.replace(":businessId", businessId)}`;
    const params = this.withPaymentProviderReadsParam({});
    return this.http.get<unknown>(url, { params }).pipe(map(normalizeWalletOverviewDto));
  }

  /** Provider-neutral wallet list (Finmo vs payment-provider tables, depending on API mode). */
  getPaymentWallets(businessId: string) {
    const url = `${this.apiUrl}${API_CONST.WALLET.GET_WALLET_FINMO.replace(":businessId", businessId)}`;
    const params = this.withPaymentProviderReadsParam({});
    return this.http.get<unknown>(url, { params }).pipe(map(normalizeProviderWalletList));
  }

  getWalletTransactions(
    businessId: string,
    page: number,
    limit: number,
    transactionType?: string,
    source?: "handshake" | "nucleus" | "all"
  ) {
    const params: Record<string, string | number> = { page, limit };
    if (transactionType) {
      params["transactionType"] = transactionType;
    }
    if (source) {
      params["source"] = source;
    }
    const url = `${this.apiUrl}${API_CONST.WALLET.GET_WALLET_TRANSACTIONS.replace(":businessId", businessId)}`;
    return this.http.get<unknown>(url, { params: this.withPaymentProviderReadsParam(params) }).pipe(map(normalizeWalletTransactionList));
  }

  getBeneficiary(businessId: string) {
    const url = `${this.apiUrl}${API_CONST.WALLET.GET_BENEFICIARY.replace(":businessId", businessId)}`;
    const params = this.withPaymentProviderReadsParam({});
    return this.http.get<unknown>(url, { params }).pipe(map((raw) => (raw == null ? null : normalizeProviderBeneficiary(raw))));
  }

  createBeneficiary(businessId: string, beneficiary: object) {
    const url = `${this.apiUrl}${API_CONST.WALLET.CREATE_BENEFICIARY.replace(":businessId", businessId)}`;
    return this.http.post<unknown>(url, beneficiary).pipe(map(normalizeProviderBeneficiary));
  }

  updateBeneficiary(businessId: string, beneficiary: object) {
    const url = `${this.apiUrl}${API_CONST.WALLET.UPDATE_BENEFICIARY.replace(":businessId", businessId)}`;
    return this.http.patch<unknown>(url, beneficiary).pipe(map(normalizeProviderBeneficiary));
  }

  getPayouts(businessId: string, page: number, limit: number) {
    const url = `${this.apiUrl}${API_CONST.WALLET.GET_PAYOUTS.replace(":businessId", businessId)}`;
    return this.http.get<unknown>(url, { params: this.withPaymentProviderReadsParam({ page, limit }) }).pipe(map(normalizePayoutList));
  }

  withdrawToBank(businessId: string, payout: object) {
    const url = `${this.apiUrl}${API_CONST.WALLET.WITHDRAW_TO_BANK.replace(":businessId", businessId)}`;
    return this.http.post<unknown>(url, payout).pipe(map(normalizeProviderPayout));
  }

  getPayoutParam(payoutMethod: string) {
    const url = `${this.apiUrl}${API_CONST.WALLET.GET_PAYOUT_PARAM.replace(":payoutMethod", payoutMethod)}`;
    return this.http.get<unknown>(url).pipe(map(normalizeProviderPayoutMethod));
  }

  validatePayoutBeneficiary(businessId: string) {
    return this.http.get<unknown>(`${this.apiUrl}${API_CONST.WALLET.VALIDATE_PAYOUT_BENEFICIARY.replace(":businessId", businessId)}`);
  }
}
